/**
 * The Activity tab — §5 of the record page plan.
 *
 * Composer at the top, what is open under it, then the timeline: everything
 * that has happened to this record, in day groups, newest first.
 *
 * The timeline draws two kinds of thing and the difference is the whole
 * design. **A person wrote this** gets a card — a surface, a border, an
 * author. **The product did this** gets a line: an outlined icon, one
 * sentence, no card. You can tell at a glance which half of the story you are
 * reading, and neither one is pretending to be the other.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ACTIVITY_FILTERS, ACTIVITY_FILTER_LABELS, ACTIVITY_KIND_LABELS,
  ACTIVITY_KIND_VERBS, KIND_STYLES, SCHEDULED_KINDS,
  type ActivityFilter, type ActivityKind, type ActivityVisibility, type RecordType,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { NoteBody } from '../components/note-editor.js'
import { RichNoteEditor } from '../components/note-rich-editor.js'
import { usePhone } from '../lib/use-narrow.js'
import { Avatar, Button, Chip, EmptyState, SectionLabel } from '../components/primitives.js'
import { IconEdit } from '../frame/icons.js'
import { RecordCard } from './record-rail.js'
import { ActivityEmpty } from './record-empties.js'
import { ActivityRow, KindTile } from './record-overview.js'
import { Glyph, type GlyphName } from '../activity/glyphs.js'
import { request } from '../lib/api.js'
import { formatRelative } from '../lib/format.js'
import { completeKey, type RecordActivity } from '../lib/use-record.js'
import { useActivityPost } from './use-activity-post.js'
import { useRecordMessage, type MessageRecord } from './record-message.js'

export interface FeedItem {
  id: string
  kind: string
  isSystem: boolean
  title: string
  body: string
  at: string
  actor: string | null
  tags: { label: string; tone: string }[]
  visibility?: ActivityVisibility
  pinnedAt?: string | null
  /** Set when it was changed after it was written — never hidden. */
  editedAt?: string | null
  activityId?: string
  /** Made at a stop: grouped under *Scoped on site · <person> · <time>* (Activity, Board 15 C). */
  scope?: { stopId: string; person: string | null }
  /** A voice note's recording. */
  audio?: { fileId: string; seconds: number | null }
  /** The photos and video from one stop: the first four, and how many more. */
  files?: { id: string; video: boolean; seconds: number | null }[]
  more?: number
}

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------

/**
 * Six kinds behind one box.
 *
 * The kind changes what the button says and, for a task or a meeting, adds
 * the two fields those need — when, and who. Everything else about the box is
 * the same, because "write something down about this record" is one action
 * whatever it turns out to have been.
 */
export interface ActivitySubmit {
  (input: {
    kind: ActivityKind; body: string; title?: string; visibility: ActivityVisibility
    dueAt?: string | null; assigneeId?: string | null
  }): Promise<void>
}

/**
 * One activity being written — the state, and what Add does with it.
 *
 * Lifted out of `ActivityComposer` with nothing changed, because the quiet
 * page's phone sheet (`quiet-records.md` §4, `QuietLogSheet`) is the *same
 * draft* in a different shape: six kinds as a 3 × 2 grid of thumb-sized
 * tiles, the two scheduled fields as 52px rows, and the primary in the
 * sheet's own sticky footer rather than inside the card.
 *
 * A second copy would have had to agree with this one about what makes a
 * note ready (body text, not markup), what a task needs instead (a title),
 * what is cleared afterwards and what deliberately is not — the kind and the
 * visibility stay, because somebody logging three calls is logging three
 * calls. It would not have agreed for long, and the symptom would have been
 * the sheet refusing an activity the card accepts.
 */
export function useActivityDraft(onSubmit: ActivitySubmit) {
  const [kind, setKind] = useState<ActivityKind>('note')
  const [body, setBody] = useState('')
  const [title, setTitle] = useState('')
  const [visibility, setVisibility] = useState<ActivityVisibility>('internal')
  const [dueAt, setDueAt] = useState('')
  const [assigneeId, setAssigneeId] = useState('')

  const scheduled = SCHEDULED_KINDS.includes(kind)
  const ready = scheduled ? title.trim().length > 0 : textOf(body).length > 0

  const submit = async () => {
    await onSubmit({
      kind,
      body: textOf(body) ? body : '',
      title: title.trim() || undefined,
      visibility,
      dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      assigneeId: assigneeId || null,
    })

    // The rich box takes an empty value whenever it is handed one — see its
    // write-back effect — so clearing here really does clear the box.
    setBody('')
    setTitle('')
    setDueAt('')
  }

  return {
    kind, setKind, body, setBody, title, setTitle, visibility, setVisibility,
    dueAt, setDueAt, assigneeId, setAssigneeId, scheduled, ready, submit,
  }
}

/**
 * Who **@** may name in a note: the page's people (the ones a task can be
 * assigned to), or — for somebody who may not list the company's people, a
 * sales rep among them — Activity's own directory, the people they see and
 * the ones they do not (`GET /activity/people`, §6.6). A teammate is
 * someone you can name whether or not you see their calendar.
 */
export function useMentionable(people: readonly { id: string; name: string }[]): readonly { id: string; name: string }[] {
  const [found, setFound] = useState<{ id: string; name: string }[]>([])
  const asked = people.length === 0
  useEffect(() => {
    if (!asked) return undefined
    let live = true
    request<{ people: { id: string; name: string }[]; locked?: { id: string; name: string }[] }>('/activity/people')
      .then((answer) => {
        if (!live) return
        const seen = new Set<string>()
        setFound([...answer.people, ...(answer.locked ?? [])]
          .filter((person) => !seen.has(person.id) && seen.add(person.id))
          .map(({ id, name }) => ({ id, name })))
      })
      .catch(() => { if (live) setFound([]) })
    return () => { live = false }
  }, [asked])
  return asked ? found : people
}

export function ActivityComposer({
  onSubmit, people, busy, subject, aim, record, onSent,
}: {
  onSubmit: ActivitySubmit
  people: { id: string; name: string }[]
  busy?: boolean
  /**
   * The record it writes on. With it, *Email* and *Text* are for somebody
   * with an Inbox only, and each asks *Send* (Activity's New email or New
   * text — Aaron, Oct 23) or *Log* (as before — Aaron, Oct 24,
   * `record-message.tsx`); without it they log, as they always did.
   */
  record?: MessageRecord
  /** A message went out from *Email* or *Text*: reload what shows it. */
  onSent?: () => void
  /**
   * Somebody pressed a step that is about this composer.
   *
   * A contact's **Send a message** picks Text or Email here rather than
   * opening a screen, because the composer is already on the page two
   * inches above the step (`quiet-records.md` §7). The `seq` carries the
   * *press*, not the kind: aiming twice at the same kind has to work twice,
   * and an effect keyed on the kind would do nothing the second time.
   */
  aim?: { kind: ActivityKind; seq: number }
  /**
   * What the record is called, for the note field's prompt.
   *
   * "Write a note about Santos Home…" rather than "Write a note…" — the
   * quiet page is the one place the composer is the whole screen rather than
   * one card on a page that already says what record this is
   * (`quiet-records.md` §4). The rest of the placeholder is unchanged, so
   * there is still one sentence and one place it is written.
   */
  subject?: string
}) {
  const {
    kind, setKind, body, setBody, title, setTitle, visibility, setVisibility,
    dueAt, setDueAt, assigneeId, setAssigneeId, scheduled, ready, submit,
  } = useActivityDraft(onSubmit)
  const message = useRecordMessage(record, onSent)
  /* The kind buttons, so the Send · Log choice hangs off the one pressed — or aimed at by a step. */
  const buttons = useRef(new Map<ActivityKind, HTMLButtonElement>())
  const pick = (next: ActivityKind) => {
    if (!message.take(next, () => setKind(next), buttons.current.get(next) ?? null)) setKind(next)
  }

  const seq = aim?.seq
  useEffect(() => { if (aim) pick(aim.kind) }, [seq])
  const phone = usePhone()
  const mentionable = useMentionable(people)

  return (
    <section className="rounded-record border border-line bg-surface p-3.5">
      <div className="flex flex-wrap gap-1.5">
        {message.kinds.map((candidate) => (
          <button
            key={candidate}
            ref={(node) => { if (node) buttons.current.set(candidate, node); else buttons.current.delete(candidate) }}
            type="button"
            aria-haspopup={record && (candidate === 'email' || candidate === 'sms') ? 'dialog' : undefined}
            onClick={() => pick(candidate)}
            className={cn(
              'flex h-8 items-center gap-1.5 rounded-button border px-2.5 text-[12.5px] font-semibold',
              candidate === kind
                ? 'border-accent-line bg-accent-soft text-accent-dark'
                : 'border-transparent text-text-muted hover:bg-hover',
            )}
          >
            <KindTile kind={candidate} size={18} />
            {ACTIVITY_KIND_LABELS[candidate]}
          </button>
        ))}
      </div>

      {scheduled && (
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={kind === 'task' ? 'What needs doing?' : 'What is the meeting?'}
          className="mt-2.5 h-9 w-full rounded-field border border-line bg-surface px-2.5 text-body text-text placeholder:text-text-faint"
        />
      )}

      {/*
        * The rich editor, not a textarea — and since Aaron's "yes" (Oct 25)
        * the task notes' box (`RichNoteEditor`, TipTap): bold, italic,
        * underline, strikethrough, a highlight, a colour, a size, both lists,
        * an indent and a link, with **@** naming somebody from `people`
        * (they are told — `services/activity/mentions.ts`). The stored body
        * is sanitised HTML on both sides, as it always was, and the timeline
        * draws it with the same `NoteBody`.
        */}
      <div className="mt-2.5">
        <RichNoteEditor
          label="Note"
          value={body}
          onChange={setBody}
          disabled={busy}
          phone={phone}
          minHeight={64}
          mentions={mentionable}
          placeholder={scheduled
            ? 'Anything else about it…'
            : `Write a note${subject ? ` about ${subject}` : ''}… @ to mention someone · drop a photo`}
        />
      </div>

      {scheduled && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <label className="flex items-center gap-1.5 text-cell text-text-muted">
            When
            <input
              type="datetime-local"
              value={dueAt}
              onChange={(event) => setDueAt(event.target.value)}
              className="h-8 rounded-field border border-line bg-surface px-2 text-cell text-text"
            />
          </label>
          <label className="flex items-center gap-1.5 text-cell text-text-muted">
            Assign to
            <select
              value={assigneeId}
              onChange={(event) => setAssigneeId(event.target.value)}
              className="h-8 rounded-field border border-line bg-surface px-2 text-cell text-text"
            >
              <option value="">Nobody</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>{person.name}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <footer className="mt-2.5 flex flex-wrap items-center gap-2">
        {/*
          * Who can see it, in words rather than a switch: "Internal" and
          * "Crew sees it" are different enough that a toggle with one label
          * would leave somebody guessing which way was which.
          */}
        <div
          role="radiogroup"
          aria-label="Who can see it"
          className="flex overflow-hidden rounded-button border border-line"
        >
          {(['internal', 'crew'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={visibility === value}
              onClick={() => setVisibility(value)}
              className={cn(
                'px-2.5 py-1.5 text-[12px] font-semibold',
                visibility === value ? 'bg-text text-surface' : 'text-text-muted hover:bg-hover',
              )}
            >
              {value === 'internal' ? 'Internal' : 'Crew sees it'}
            </button>
          ))}
        </div>

        <Button
          variant="primary"
          className="ml-auto"
          disabled={!ready || busy}
          onClick={() => void submit()}
        >
          {busy ? 'Saving…' : ACTIVITY_KIND_VERBS[kind]}
        </Button>
      </footer>
      {message.sheet}
    </section>
  )
}

/** The words out of a note's markup — `<p><br></p>` is an empty note. */
const textOf = (html: string): string =>
  html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()

// ---------------------------------------------------------------------------
// Upcoming
// ---------------------------------------------------------------------------

export function UpcomingCard({
  items, onComplete, busy,
}: {
  items: RecordActivity[]
  onComplete: (id: string) => void
  busy?: string | null
}) {
  if (items.length === 0) return null
  return (
    <RecordCard title="Upcoming" count={items.length}>
      {/* A repeating meeting's row is its next occurrence: the tick names that one (`completeKey`, 0152). */}
      {items.map((item) => (
        <ActivityRow
          key={completeKey(item)}
          item={item}
          busy={busy === completeKey(item)}
          onComplete={() => onComplete(completeKey(item))}
        />
      ))}
    </RecordCard>
  )
}

// ---------------------------------------------------------------------------
// The timeline
// ---------------------------------------------------------------------------

export function ActivityTimeline({
  object, id, filter, onFilter, reloadKey, subject, people,
}: {
  object: RecordType
  id: string
  filter: ActivityFilter
  onFilter: (filter: ActivityFilter) => void
  /** Bumped by the composer, so a new note appears without a page reload. */
  reloadKey: number
  /** What the record is called, for the empty state's one sentence (§8). */
  subject?: string
  /** Who *@* may name in a note being corrected — the composer's own list. */
  people?: readonly { id: string; name: string }[]
}) {
  const [items, setItems] = useState<FeedItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const feed = await request<{ items: FeedItem[] }>(
        `/records/${object}/${id}/activity?filter=${filter}`)
      setItems(feed.items)
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be loaded.')
    }
  }, [object, id, filter])

  useEffect(() => { void load() }, [load, reloadKey])

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {/*
        * One row that scrolls, not two that wrap. Seven chips are 620px and a
        * phone is 390 — wrapping put "System" on a line of its own and pushed
        * the first item of the timeline below the fold on every record.
        */}
      <div className="no-scrollbar flex items-center gap-1.5 overflow-x-auto">
        {ACTIVITY_FILTERS.map((candidate) => (
          <Chip
            key={candidate}
            active={candidate === filter}
            onClick={() => onFilter(candidate)}
            className="shrink-0"
          >
            {ACTIVITY_FILTER_LABELS[candidate]}
          </Chip>
        ))}
      </div>

      {error && <EmptyState title="This could not load" body={error} />}

      {/*
        * §8: the composer is already at the top of this tab, so the empty
        * state adds a sentence and three ghosts and no button — one that
        * scrolled somebody to a box already on their screen is a button that
        * teaches them the tab is not listening.
        */}
      {items && items.length === 0 && !error && (
        <ActivityEmpty
          subject={subject ?? 'this record'}
          {...(filter === 'all' ? {} : { filtered: true })}
        />
      )}

      {items && groupByDay(items).map((group) => (
        <div key={group.label} className="flex flex-col">
          <SectionLabel className="px-1 pb-1.5 pt-2">{group.label}</SectionLabel>
          {byStop(group.items).map((part) => (
            <div key={part.key} className="flex flex-col">
              {part.scope && (
                <SectionLabel className="px-1 pb-1 pt-2">
                  {`Scoped on site${part.scope.person ? ` · ${part.scope.person}` : ''} · ${clockOf(part.items[0]!.at)}`}
                </SectionLabel>
              )}
              {part.items.map((item) => (
                <FeedRow
                  key={item.id}
                  item={item}
                  {...(people ? { people } : {})}
                  onEdited={async (body) => {
                    await request(`/activities/${item.activityId}`, {
                      method: 'PATCH',
                      body: { body },
                    })
                    await load()
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * One item. A card when a person wrote it, a line when the product did it.
 *
 * The connector is a 2px rule from the icon down to the next item, which is
 * what makes a column of mixed cards and lines read as one thread rather than
 * as a pile of unrelated boxes.
 */
function FeedRow({
  item, onEdited, people,
}: {
  item: FeedItem
  onEdited: (body: string) => Promise<void>
  /** Who *@* may name while the note is corrected. */
  people?: readonly { id: string; name: string }[]
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function save() {
    if (draft === null) return
    setSaving(true)
    try {
      await onEdited(draft)
      setDraft(null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex min-w-0 gap-3">
      <div className="flex shrink-0 flex-col items-center">
        {item.isSystem
          ? (
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full border border-line bg-surface"
              style={{ color: `rgb(var(--status-activity-${tileKind(item.kind)}-fg))` }}
            >
              <KindTile kind={tileKind(item.kind)} size={18} />
            </span>
          )
          : item.scope && stopTile(item)
            ? <StopTile {...stopTile(item)!} />
            : <KindTile kind={item.kind === 'files' ? 'system' : item.kind} size={32} />}
        <span aria-hidden className="w-0.5 flex-1 bg-line-soft" />
      </div>

      {item.isSystem
        ? (
          <div className="min-w-0 flex-1 py-1.5">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <b className="truncate text-body font-semibold text-text">{item.title}</b>
              <Tags tags={item.tags} />
              <span className="ml-auto shrink-0 text-chip text-text-faint">
                {item.actor ?? 'system'} · {formatRelative(item.at)}
              </span>
            </div>
            {item.body && <p className="m-0 text-body text-text-body">{item.body}</p>}
          </div>
        )
        : (
          <div className="mb-2 min-w-0 flex-1 rounded-[12px] border border-line bg-surface px-3.5 py-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <b className="truncate text-body font-semibold text-text">{item.title}</b>
              {/*
                * The Crew tag comes from the feed, not from here. Both drew it
                * for a while and a crew note wore the word twice.
                */}
              {item.pinnedAt && <Tags tags={[{ label: 'Pinned', tone: 'meeting' }]} />}
              <Tags tags={item.tags} />
              <span className="ml-auto shrink-0 text-chip text-text-faint">
                {item.actor ?? 'Someone'} · {formatRelative(item.at)}
              </span>
            </div>
            {draft !== null
              ? (
                <div className="mt-1.5 flex flex-col gap-2">
                  <RichNoteEditor label="Note" value={draft} onChange={setDraft} disabled={saving} minHeight={64}
                    {...(people ? { mentions: people } : {})} />
                  <div className="flex gap-2">
                    <Button variant="primary" disabled={saving} onClick={() => void save()}>
                      {saving ? 'Saving…' : 'Save note'}
                    </Button>
                    <Button onClick={() => setDraft(null)}>Cancel</Button>
                  </div>
                </div>
              )
              : item.body && (
                /*
                 * Written here means rich text; arrived here means plain. An
                 * email's body is the text we sent, and running it through the
                 * note renderer would let a customer's own `<` in a subject
                 * line decide how our timeline looks. `activityId` is the
                 * exact test: it is set only on what a person typed into this
                 * product.
                 */
                item.activityId
                  ? <NoteBody html={item.body} className="mt-1 leading-[1.45]" />
                  : (
                    <p className="m-0 mt-1 whitespace-pre-wrap text-body leading-[1.45] text-text-body">
                      {item.body}
                    </p>
                  )
              )}

            {item.audio && draft === null && <VoicePlayer audio={item.audio} />}
            {item.files && <ScopeFiles files={item.files} more={item.more ?? 0} />}

            {/*
              * A correction says it is one. §4.2: an edit that left no trace
              * would be a rewrite, and the audit row nobody opens is not the
              * same as the line on the card everybody reads.
              */}
            {item.editedAt && draft === null && (
              <p className="m-0 mt-1.5 text-[11px] text-text-faint">
                Edited {formatRelative(item.editedAt)}
              </p>
            )}

            {item.activityId && draft === null && (
              <button
                type="button"
                aria-label="Edit this note"
                onClick={() => setDraft(item.body)}
                className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-semibold text-text-muted hover:text-accent"
              >
                <IconEdit size={12} />
                Edit
              </button>
            )}
          </div>
        )}
    </div>
  )
}

function Tags({ tags }: { tags: { label: string; tone: string }[] }) {
  if (tags.length === 0) return null
  return (
    <>
      {tags.map((tag) => (
        <span
          key={tag.label}
          className="shrink-0 rounded-tag px-1.5 py-px text-[10.5px] font-semibold capitalize"
          style={{
            background: `rgb(var(--status-activity-${toneKind(tag.tone)}-bg))`,
            color: `rgb(var(--status-activity-${toneKind(tag.tone)}-fg))`,
          }}
        >
          {tag.label}
        </span>
      ))}
    </>
  )
}

/** The feed's kinds are wider than the composer's; everything else is a note. */
/**
 * What was made at a stop wears Activity's look (Board 15 C, §3): a voice
 * note the mic, the photos and video a camera, both in Note's grey, and the
 * call §3's purple phone. Everything else keeps the record feed's tiles.
 */
function stopTile(item: FeedItem): { glyph: GlyphName; fg: string; bg: string } | null {
  if (item.kind === 'files') return { glyph: 'camera', fg: KIND_STYLES.note.fg, bg: KIND_STYLES.note.bg }
  if (item.kind === 'note' && (item.audio || item.title === 'Voice note')) {
    return { glyph: 'mic', fg: KIND_STYLES.voice_note.fg, bg: KIND_STYLES.voice_note.bg }
  }
  if (item.kind === 'call') return { glyph: 'phone', fg: KIND_STYLES.call.fg, bg: KIND_STYLES.call.bg }
  return null
}

function StopTile({ glyph, fg, bg }: { glyph: GlyphName; fg: string; bg: string }) {
  return (
    <span aria-hidden data-stop-tile={glyph} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-field"
      style={{ background: bg, color: fg }}>
      <Glyph name={glyph} size={17} />
    </span>
  )
}

const tileKind = (kind: string): string =>
  (['note', 'task', 'call', 'email', 'sms', 'meeting'].includes(kind) ? kind : 'system')

/** A tag's tone, in the same six colours, so nothing new is invented. */
const toneKind = (tone: string): string => ({
  info: 'task', warn: 'meeting', purple: 'email', danger: 'call',
  neutral: 'system', meeting: 'meeting',
}[tone] ?? 'system')

/**
 * What was made at one stop sits together, at the place of its newest item,
 * under its own label (Activity §11.3, Board 15 C); everything else keeps its
 * place in time.
 */
function byStop(items: FeedItem[]): { key: string; scope: FeedItem['scope'] | null; items: FeedItem[] }[] {
  const out: { key: string; scope: FeedItem['scope'] | null; items: FeedItem[] }[] = []
  const stops = new Map<string, { key: string; scope: FeedItem['scope'] | null; items: FeedItem[] }>()
  for (const item of items) {
    if (!item.scope) {
      out.push({ key: item.id, scope: null, items: [item] })
      continue
    }
    const found = stops.get(item.scope.stopId)
    if (found) found.items.push(item)
    else {
      const part = { key: `stop:${item.scope.stopId}`, scope: item.scope, items: [item] }
      stops.set(item.scope.stopId, part)
      out.push(part)
    }
  }
  return out
}

/** *10:58 AM*. */
const clockOf = (at: string): string =>
  new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

/** *0:48*. */
const lengthOf = (seconds: number | null): string =>
  (seconds === null ? '' : `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`)

/** A voice note's player (Board 15 C): play, the level bars, the length. */
function VoicePlayer({ audio }: { audio: NonNullable<FeedItem['audio']> }) {
  const [playing, setPlaying] = useState(false)
  const [element, setElement] = useState<HTMLAudioElement | null>(null)
  const bars = [6, 8, 7, 6, 8, 8, 7, 13, 18, 19, 16, 8, 9, 15, 15, 12, 8, 6, 6, 9, 12, 13, 10, 6, 14, 19]
  return (
    <div className="mt-2 flex w-fit items-center gap-2 rounded-full border border-line-faint bg-surface-alt py-1.5 pl-1.5 pr-2.5">
      <button type="button" aria-label={playing ? 'Pause' : 'Play'}
        onClick={() => {
          if (!element) return
          if (playing) element.pause()
          else void element.play()
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent text-white">
        <svg viewBox="0 0 24 24" width={13} height={13} fill="currentColor" aria-hidden>
          {playing ? <path d="M7 5h4v14H7zM13 5h4v14h-4z" /> : <path d="M8 5v14l11-7z" />}
        </svg>
      </button>
      <span aria-hidden className="flex h-5 items-center gap-[3px]">
        {bars.map((height, index) => <i key={index} className="block w-1 rounded-[3px] bg-line-strong" style={{ height }} />)}
      </span>
      <span className="text-[11.5px] font-semibold text-text-muted">{lengthOf(audio.seconds)}</span>
      <audio ref={setElement} src={`/api/v1/files/${audio.fileId}`} preload="none"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
    </div>
  )
}

/** The photos and video from one stop: four 52 px tiles and *+n* (Board 15 C). */
function ScopeFiles({ files, more }: { files: NonNullable<FeedItem['files']>; more: number }) {
  return (
    <div className="mt-1.5 flex gap-[5px]">
      {files.map((file) => (
        <a key={file.id} href={`/api/v1/files/${file.id}`} target="_blank" rel="noreferrer"
          className="relative inline-block h-[52px] w-[52px] shrink-0 overflow-hidden rounded-[7px] bg-line-soft">
          {file.video ? (
            <span className="absolute inset-0 flex items-center justify-center bg-line-strong text-white">
              <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden><path d="M8 5v14l11-7z" /></svg>
            </span>
          ) : (
            <img src={`/api/v1/files/${file.id}`} alt="" loading="lazy" className="h-full w-full object-cover" />
          )}
        </a>
      ))}
      {more > 0 && (
        <span className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-[7px] bg-line-soft text-[12px] font-bold text-text-muted">
          +{more}
        </span>
      )}
    </div>
  )
}

/** `Today`, `Yesterday`, then the date — §5.4. */
function groupByDay(items: FeedItem[]): { label: string; items: FeedItem[] }[] {
  const out: { label: string; items: FeedItem[] }[] = []
  const today = new Date().toDateString()
  const yesterday = new Date(Date.now() - 86_400_000).toDateString()

  for (const item of items) {
    const day = new Date(item.at).toDateString()
    const label = day === today ? 'Today'
      : day === yesterday ? 'Yesterday'
        : new Date(item.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const group = out[out.length - 1]
    if (group?.label === label) group.items.push(item)
    else out.push({ label, items: [item] })
  }
  return out
}

/** The avatar beside a note, when a feed wants one. */
export function FeedAuthor({ name }: { name: string | null }) {
  return <Avatar name={name ?? 'Someone'} size={28} />
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

/**
 * Composer, Upcoming, timeline — the whole Activity tab, for every record.
 *
 * It is a component rather than three in each module's page because the
 * *order* is part of the design: write first, then what is owed, then what
 * happened. A module that assembled its own would sooner or later put the
 * composer at the bottom, and then Activity would mean something different
 * on jobs than on contacts.
 */
export function RecordActivityTab({
  object, id, people, upcoming, onComplete, onChanged, busyId, subject,
}: {
  object: RecordType
  id: string
  people: { id: string; name: string }[]
  upcoming: RecordActivity[]
  onComplete: (activityId: string) => void
  /** The summary owns the counts, so the page reloads it after a write. */
  onChanged: () => void
  busyId?: string | null
  /**
   * What the record is called — the quiet config's `composerSubject`.
   *
   * The same word the panel's prompt uses, passed through rather than
   * composed again, so "Write a note about Santos Home…" and "…about Santos
   * Home collect here" cannot disagree about what this record is called.
   */
  subject?: string
}) {
  const [filter, setFilter] = useState<ActivityFilter>('all')
  /* The write itself is `useActivityPost`, because the quiet Overview carries
     the same composer and has to make the same write (`quiet-records.md` §4). */
  const { post, busy, reloadKey, refresh } = useActivityPost(object, id, onChanged)
  const mentionable = useMentionable(people)

  return (
    <>
      <ActivityComposer
        onSubmit={post}
        people={people}
        busy={busy}
        record={{ object, id }}
        onSent={refresh}
        {...(subject ? { subject } : {})}
      />
      <UpcomingCard items={upcoming} onComplete={onComplete} busy={busyId} />
      <ActivityTimeline
        {...(subject ? { subject } : {})}
        people={mentionable}
        object={object}
        id={id}
        filter={filter}
        onFilter={setFilter}
        reloadKey={reloadKey}
      />
    </>
  )
}
